import React from 'react'
import { useLocation } from 'react-router-dom'
import { Paragraph } from 'src/components'
import { Aside, Main } from 'src/components/Layout'
import EmailHelpBox from 'src/components/Layout/EmailHelpBox'
import { FAQ } from './FAQ'
import { Link } from './Link'

const FAQS: {
  id?: string
  question: string
  answer: JSX.Element | string
  visible: 'ALL' | 'ADMIN' | 'USER'
}[] = [
  {
    question: 'How can I check if my Amazon Connect is SAML or CCP?',
    visible: 'ADMIN',
    answer: (
      <>
        <Paragraph>
          {
            "Login to your Amazon Connect instance, then go to Users > Users Management > Add User. If you're able to set a primary email address for this user it's CCP, otherwise it's SAML"
          }
        </Paragraph>
        <div className="flex items-start flex-col md:flex-row">
          <img
            className="border-2"
            src={require('src/images/ccp_user.png')}
            width="350"
            alt="login screen"
          />
          <img
            className="border-2 mt-2 md:mt-0 md:ml-2"
            src={require('src/images/saml_user.png')}
            width="350"
            alt="login screen"
          />
        </div>
      </>
    ),
  },
  {
    question: 'Where can I find the apps?',
    visible: 'ALL',
    answer: (
      <>
        <Link label="iOS:" url="https://apps.apple.com/app/id1625067813" />
        <br />
        <Link
          label="Android:"
          url="https://play.google.com/store/apps/details?id=be.idealsystems.mc.amazon"
        />
      </>
    ),
  },
  {
    id: "Don't have a code yet?", // app still use this id, so can't change it
    question: "Don't have an organization yet?",
    visible: 'ALL',
    answer: (
      <Paragraph>
        Your administrator should create an organization at{' '}
        <Link
          label="AWS Marketplace"
          url="https://aws.amazon.com/marketplace/pp/prodview-muqeoaaiwp3ic"
          showLabel
        />{' '}
        first, afterwards they can create an account for you. In the app you'll
        need the name of the provisioned organization, afterwards you can login
        with your credentials.
      </Paragraph>
    ),
  },
  {
    question: "Which OS's are supported?",
    visible: 'ALL',
    answer:
      'The app supports the 2 latest version of Android (11 & 12) & iOS (14 & 15)',
  },
  {
    question: 'Which channels are available?',
    visible: 'ALL',
    answer:
      'The app currently supports the following channels: Voice, Chats & Tasks',
  },
  {
    question: 'Agent information error during login?',
    visible: 'ALL',
    answer: (
      <>
        If the error message looks like:{' '}
        <i>
          A problem occurred while getting agent information, check security
          profile.
        </i>
        <br />
        Then you'll need to add the following <b>view permission</b> to the{' '}
        <b>Users and permissions</b> in <b>Amazon Connect</b>
        <ul className="ml-4 list-disc">
          <li>Users</li>
        </ul>
      </>
    ),
  },
  {
    question: 'Not able to search contacts?',
    visible: 'ALL',
    answer: (
      <>
        If the error message looks like:{' '}
        <i>User xxx is not allowed to perform: ContactSearch.View</i>
        <br />
        Then you'll need to add the following <b>view permissions</b> to the{' '}
        <b>Agent profile</b> in <b>Amazon Connect</b>
        <ul className="ml-4 list-disc">
          <li>Contact search</li>
          <li>Search contacts by keywords</li>
          <li>Contact attributes</li>
        </ul>
      </>
    ),
  },
  {
    question: 'Why am I getting billed?',
    visible: 'ALL',
    answer: (
      <>
        Users need to properly logout of the app, changing the status to offline
        isn't enough. <br />
        You can logout in the settings menu.
      </>
    ),
  },
  {
    question: `Why can't I accept voice interactions while also using CCP in the browser?`,
    visible: 'ALL',
    answer: (
      <>
        Amazon Connect doesn't support multiple windows at the same time, so you
        can't be logged in with the app & the website at the same time.
        <br />
        You should logout of the website before logging into the app.
      </>
    ),
  },
]

type Props = {
  isAdmin?: boolean
}

export function HelpWithoutNavbar(props: Props) {
  const { hash } = useLocation()
  const currentQuestion = decodeURIComponent(hash.slice(1))
  const visibleFAQS = React.useMemo(() => {
    const visibilityFilter = ['ALL', props.isAdmin ? 'ADMIN' : 'USER'] as const
    return FAQS.filter((item) => visibilityFilter.includes(item.visible)).map(
      (item) => ({
        ...item,
        id: item.id || item.question,
      }),
    )
  }, [props.isAdmin])

  return (
    <>
      {props.isAdmin && <EmailHelpBox className="flex-1" />}
      {visibleFAQS.map((item) => (
        <FAQ
          key={item.question}
          id={item.id}
          question={item.question}
          isActive={item.id === currentQuestion}
          withNavbar={props.isAdmin}
        >
          {item.answer}
        </FAQ>
      ))}
    </>
  )
}

export default function Help(props: Props) {
  return (
    <>
      <Aside title="Help" hideLogin hideExtraRow />

      <Main>
        <HelpWithoutNavbar isAdmin={props.isAdmin} />
      </Main>
    </>
  )
}
